import { GetUserBasket } from "@ac-expo/api-types";
import { getName } from "@ac-expo/utils";
import { useMemo } from "react";

export type ProfesionnalMappingValue = {
  id: string;
  name: string;
};

type Props = {
  cart: GetUserBasket | undefined;
};

export function useConsent({ cart }: Props) {
  const { organisersMapping, structuresMapping } = useMemo(() => {
    const organisersMapping = new Map<string, ProfesionnalMappingValue>();
    const structuresMapping = new Map<string, ProfesionnalMappingValue>();

    cart?.basketItem?.forEach((item) => {
      const buyable = item.buyableItem;
      const ticketType = buyable?.ticketType;

      if (ticketType?.event) {
        const { event, activity } = ticketType;
        const { organiser } = event;
        const organiserItem = organisersMapping.get(organiser.id);

        if (activity && activity.structure) {
          const { structure } = activity;
          const structureItem = structuresMapping.get(structure.id);

          if (!structureItem && structure && structure.admin) {
            const { admin } = structure;
            const name =
              admin.businessName ||
              getName({
                firstName: admin.firstName,
                lastName: admin.lastName,
              });

            structuresMapping.set(structure.id, {
              id: structure.id,
              name,
            });
          }
        } else if (!organiserItem && organiser) {
          const { admin } = organiser;
          const name =
            admin.businessName ||
            getName({
              firstName: admin.firstName,
              lastName: admin.lastName,
            });

          organisersMapping.set(organiser.id, {
            id: organiser.id,
            name,
          });
        }
      }
    });

    return { organisersMapping, structuresMapping };
  }, [cart?.basketItem]);

  const profesionnalNames = Array.from(
    new Set([
      ...Array.from(organisersMapping.values()).map((item) => item.name),
      ...Array.from(structuresMapping.values()).map((item) => item.name),
    ])
  ).join(", ");

  const organiserIds = useMemo(() => Array.from(organisersMapping.keys()), [organisersMapping]);
  const structureIds = useMemo(() => Array.from(structuresMapping.keys()), [structuresMapping]);

  return {
    profesionnalNames,
    organiserIds,
    structureIds,
    organisersMapping,
    structuresMapping,
  };
}
